import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Code2, Loader2, Mail, KeyRound, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [devToken, setDevToken] = useState(null);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      setDevToken(data.dev_token);
      toast.success("Anfrage gesendet.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 bg-primary border-2 border-black rounded-lg flex items-center justify-center nb-shadow-sm">
            <Code2 className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-heading font-black text-xl">CodeQuest</span>
        </Link>
        <div className="nb-card p-6">
          <div className="h-12 w-12 bg-warning border-2 border-black rounded-xl flex items-center justify-center nb-shadow-sm mb-4">
            <KeyRound className="h-6 w-6" strokeWidth={2.5} />
          </div>
          <h1 className="font-heading font-black text-2xl mb-2">Passwort vergessen?</h1>
          <p className="text-muted-foreground font-medium mb-6 text-sm">
            Gib deine E-Mail ein und wir erstellen einen Reset-Token.
          </p>

          {!sent ? (
            <form onSubmit={submit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={2.5} />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email"
                  className="w-full pl-10 pr-4 py-3 border-2 border-black rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="du@beispiel.de"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit"
                className="w-full flex items-center justify-center gap-2 bg-primary text-white font-bold py-3 rounded-lg border-2 border-black nb-shadow nb-press disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} />}
                Reset-Token anfordern
              </button>
            </form>
          ) : (
            <div className="space-y-4" data-testid="forgot-success">
              <div className="bg-muted border-2 border-black rounded-lg p-3 text-sm font-medium">
                Falls die E-Mail existiert, wurde ein Token erstellt.
                {devToken && (
                  <>
                    <p className="mt-2 font-bold">Demo-Token (normalerweise per E-Mail):</p>
                    <code className="block break-all mt-1 text-xs bg-white border-2 border-black rounded p-2">{devToken}</code>
                  </>
                )}
              </div>
              <Link
                to={devToken ? `/passwort-reset?token=${encodeURIComponent(devToken)}` : "/passwort-reset"}
                data-testid="goto-reset"
                className="w-full flex items-center justify-center gap-2 bg-secondary text-white font-bold py-3 rounded-lg border-2 border-black nb-shadow nb-press"
              >
                Passwort jetzt zurücksetzen
                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </Link>
            </div>
          )}

          <p className="text-center mt-6 font-medium text-sm">
            <Link to="/login" className="font-bold text-secondary underline" data-testid="back-to-login">
              Zurück zum Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
